"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  FileText,
  Receipt,
  FolderKanban,
  Menu,
  ShoppingCart,
  Package,
  Settings,
  LogOut,
  Sparkles,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/providers/auth-provider";

const mobileNavItems = [
  { href: "/", icon: LayoutDashboard, label: "Accueil" },
  { href: "/tiers", icon: Users, label: "Tiers" },
  { href: "/devis/nouveau", icon: Sparkles, label: "Devis IA" },
  { href: "/factures", icon: Receipt, label: "Factures" },
];

const menuItems = [
  { href: "/chantiers", icon: FolderKanban, label: "Chantiers" },
  { href: "/commandes", icon: ShoppingCart, label: "Commandes" },
{ href: "/commandes-fournisseurs", icon: Package, label: "Cmd Fournisseurs" },
  { href: "/produits", icon: Package, label: "Produits" },
];

const quickActions = [
  { href: "/devis/nouveau", icon: FileText, label: "Nouveau devis", color: "bg-blue-500" },
  { href: "/tiers/nouveau", icon: Plus, label: "Nouveau client", color: "bg-green-500" },
  { href: "/chantiers/nouveau", icon: Plus, label: "Nouveau chantier", color: "bg-purple-500" },
];

export function MobileNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    router.push("/login");
  };

  return (
    <>
      {/* Bottom Navigation */}
      <nav className="fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 md:hidden">
        <div className="flex items-center justify-around px-1 pb-safe">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              pathname === item.href ||
              (item.href !== "/" && pathname.startsWith(item.href));

            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-muted-foreground transition-all active:scale-95",
                  "min-h-[60px] min-w-[60px]", // Touch-friendly 60x60px
                  isActive && "text-primary"
                )}
              >
                <motion.div
                  initial={false}
                  animate={{
                    scale: isActive ? 1 : 0.9,
                    backgroundColor: isActive
                      ? "hsl(var(--primary) / 0.1)"
                      : "transparent",
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  className="flex h-11 w-11 items-center justify-center rounded-2xl"
                >
                  <Icon
                    className={cn(
                      "h-5 w-5 transition-all",
                      isActive && "scale-110"
                    )}
                  />
                </motion.div>
                <span
                  className={cn(
                    "text-[10px] font-medium transition-all",
                    isActive && "font-semibold"
                  )}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}

          {/* Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <button
                className={cn(
                  "flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-muted-foreground transition-all active:scale-95",
                  "min-h-[60px] min-w-[60px]" // Touch-friendly
                )}
              >
                <motion.div
                  whileTap={{ scale: 0.95 }}
                  className="flex h-11 w-11 items-center justify-center rounded-2xl"
                >
                  <Menu className="h-5 w-5" />
                </motion.div>
                <span className="text-[10px] font-medium">Menu</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>

              <div className="mt-6 space-y-6 overflow-y-auto pb-6">
                {/* Quick Actions */}
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
                    Actions rapides
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {quickActions.map((action) => {
                      const Icon = action.icon;
                      return (
                        <Link
                          key={action.href}
                          href={action.href}
                          onClick={() => setIsOpen(false)}
                          className="flex flex-col items-center gap-2 rounded-xl border bg-card p-4 text-center transition-all hover:bg-accent active:scale-95"
                        >
                          <div
                            className={cn(
                              action.color,
                              "flex h-12 w-12 items-center justify-center rounded-full text-white"
                            )}
                          >
                            <Icon className="h-6 w-6" />
                          </div>
                          <span className="text-xs font-medium">
                            {action.label}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                <Separator />

                {/* Navigation Links */}
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
                    Navigation
                  </h3>
                  <div className="space-y-1">
                    {menuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive =
                        pathname === item.href || pathname.startsWith(item.href);
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-all active:scale-98",
                            "min-h-[48px]", // Touch-friendly
                            isActive
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          )}
                        >
                          <Icon className="h-5 w-5" />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>

                <Separator />

                {/* Settings & Logout */}
                <div className="space-y-1">
                  <Link
                    href="/parametres"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-muted-foreground transition-all hover:bg-accent hover:text-accent-foreground active:scale-98 min-h-[48px]"
                  >
                    <Settings className="h-5 w-5" />
                    Paramètres
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-red-600 transition-all hover:bg-red-50 active:scale-98 min-h-[48px] dark:text-red-400 dark:hover:bg-red-950/20"
                  >
                    <LogOut className="h-5 w-5" />
                    Déconnexion
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Spacer for fixed bottom nav */}
      <div className="h-[76px] md:hidden" />
    </>
  );
}
